import {
  castErrorResult,
  createSuccessResult,
  SimpleResult,
} from "@prostocheck/shared";

import { IShiftItem, requestShifts } from "../commands";
import { mapWithConcurrency } from "../concurrency";
import { IClosedShift, IReadPeriodParams } from "./shifts.models";
import { mergeShifts, toShiftWindows } from "./shifts.utils";

/**
 * Shifts are the only way into a period: there is no command that lists
 * receipts or Z-reports over a date range. Everything a report needs hangs off
 * this call — see ai/docs/prro-api.md.
 *
 * The server refuses a window wider than a month, so a longer period is split
 * and merged here rather than failing in the user's face.
 */
export const readShifts = async ({
  client,
  registrarFiscalNumber,
  from,
  to,
}: IReadPeriodParams): Promise<SimpleResult<IShiftItem[]>> => {
  const windows = toShiftWindows({ from, to });

  const results = await mapWithConcurrency({
    items: windows,
    map: window =>
      requestShifts({
        client,
        registrarFiscalNumber,
        from: window.from,
        to: window.to,
      }),
  });

  // One failed window makes the period incomplete, and a total that silently
  // omits a month is worse than no total at all.
  const failed = results.find(result => !result.success);

  if (failed) {
    return castErrorResult(failed);
  }

  return createSuccessResult(
    mergeShifts(results.map(result => result.data?.Shifts ?? [])),
  );
};

/**
 * Only closed shifts carry a Z-report. An open shift is not missing data — its
 * totals are readable through `LastShiftTotals` instead.
 */
export const toClosedShifts = (shifts: IShiftItem[]): IClosedShift[] =>
  shifts
    .filter(shift => Boolean(shift.ZRepFiscalNum) && Boolean(shift.Closed))
    .map(shift => ({
      shiftId: shift.ShiftId,
      openedAt: shift.Opened,
      closedAt: shift.Closed!,
      zReportFiscalNumber: shift.ZRepFiscalNum!,
      isTesting: shift.Testing,
    }));
